import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  LoaderCircle,
  RefreshCw,
} from "lucide-react";

import {
  createCheckout,
  exchangeHandoff,
  getBalance,
  getOrderStatus,
  getWebShopConfig,
  listWebShopOffers,
  loadLastOrder,
  loadSession,
  normalizeXsollaCountryPreference,
  normalizeXsollaLocalePreference,
  saveLastOrder,
  saveSession,
  type OrderStatus,
  type ShopSession,
  type WalletBalance,
  type WebShopOffer,
} from "./api";
import HomePage from "./HomePage";
import {
  formatMessage,
  formatNumber,
  getHeaderBannerImage,
  messages,
  persistSupportedLocale,
  resolveInitialLocale,
  type SupportedLocale,
} from "./i18n";

const PRODUCTS = [
  {
    offerCode: "shop_gem_pack_1",
    gems: 80,
    title: "Some Gems",
    image: "ui_icon_shopgemset_01.png",
  },
  {
    offerCode: "shop_gem_pack_2",
    gems: 500,
    title: "Pile of Gems",
    image: "ui_icon_shopgemset_02.png",
  },
  {
    offerCode: "shop_gem_pack_3",
    gems: 1200,
    title: "Heap of Gems",
    image: "ui_icon_shopgemset_03.png",
  },
  {
    offerCode: "shop_gem_pack_4",
    gems: 2500,
    title: "Bag of Gems",
    image: "ui_icon_shopgemset_04.png",
  },
  {
    offerCode: "shop_gem_pack_5",
    gems: 6500,
    title: "Bucket of Gems",
    image: "ui_icon_shopgemset_05.png",
  },
  {
    offerCode: "shop_gem_pack_6",
    gems: 14000,
    title: "Chest of Gems",
    image: "ui_icon_shopgemset_06.png",
  },
] as const;

const allowedOfferCodes = new Set(PRODUCTS.map((product) => product.offerCode));
const GEMS_ASSET_BASE = `${import.meta.env.BASE_URL}shop-assets/gems/`;
type ProductView = {
  offerCode: string;
  gems: number;
  title: string;
  image: string;
};

function gemAsset(name: string): string {
  return `${GEMS_ASSET_BASE}${name}`;
}

function formatOfferPrice(locale: SupportedLocale, offer?: WebShopOffer): string {
  const price = offer?.price;
  if (!price) return "-";
  const amount = Number(price.amount);
  if (!Number.isFinite(amount)) {
    return `${price.currency} ${price.amount}`;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: price.currency,
    }).format(amount);
  } catch {
    return `${price.currency} ${price.amount}`;
  }
}

function getOfferButtonLabel(
  locale: SupportedLocale,
  unavailableLabel: string,
  offer?: WebShopOffer,
): string {
  if (!offer?.price || offer.can_be_bought === false) return unavailableLabel;
  return formatOfferPrice(locale, offer);
}

function getOfferSubName(offer?: WebShopOffer): string | undefined {
  const subName = offer?.attributes?.sub_name;
  return typeof subName === "string" && subName.trim()
    ? subName.trim()
    : undefined;
}

function getOfferPop(offer?: WebShopOffer): string | undefined {
  const pop = offer?.attributes?.pop;
  return typeof pop === "string" && pop.trim() ? pop.trim() : undefined;
}

function useSession() {
  const [session, setSession] = useState<ShopSession | null>(() =>
    loadSession(),
  );

  return { session, setSession };
}

function Shell({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale: SupportedLocale;
}) {
  return (
    <main
      className="shop-shell min-h-screen text-white"
      style={
        {
          "--shop-bg-base": `url("${gemAsset("ui_bg_basecolor_shop.png")}")`,
          "--shop-bg-gradation": `url("${gemAsset("ui_bg_gradation_purple.png")}")`,
          "--shop-bg-pattern": `url("${gemAsset("ui_bg_pattern_02.png")}")`,
          "--shop-bg-circles": `url("${gemAsset("ui_bg_cicle_set_shop.png")}")`,
        } as React.CSSProperties
      }
    >
      <div className="shop-page-frame flex min-h-screen flex-col px-2 py-3 sm:px-4 sm:py-4">
        <div className="flex flex-1 flex-col py-3 sm:py-5">{children}</div>
      </div>
    </main>
  );
}

function EmptyState({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="shop-empty mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-white/15 text-white">
        {icon}
      </div>
      <h1 className="shop-stroked-text text-xl font-black">{title}</h1>
      {children ? <div className="mt-3 text-sm text-white/80">{children}</div> : null}
    </section>
  );
}

function getSafeNextRoute(rawNext: string | null, fallback: "/" | "/shop") {
  if (!rawNext) return fallback;
  return rawNext === "/shop" ? "/shop" : "/";
}

function HandoffPage({
  setSession,
  setLocale,
  defaultNext,
  locale,
}: {
  setSession: (s: ShopSession) => void;
  setLocale: (locale: SupportedLocale) => void;
  defaultNext: "/" | "/shop";
  locale: SupportedLocale;
}) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const text = messages[locale];

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = params.get("handoff_token");
    const projectId = params.get("project_id");
    const nextRoute = getSafeNextRoute(params.get("next"), defaultNext);
    const country = normalizeXsollaCountryPreference(params.get("country"));
    const xsollaLocale = normalizeXsollaLocalePreference(params.get("locale"));
    const uiLocale = persistSupportedLocale(xsollaLocale);
    if (uiLocale) setLocale(uiLocale);
    const handoffText = messages[uiLocale ?? locale];
    window.history.replaceState(null, "", window.location.pathname);

    if (!token || !projectId) {
      setError(handoffText.openFromAppMessage);
      return;
    }

    exchangeHandoff(token, projectId)
      .then((nextSession) => {
        const sessionWithPreferences: ShopSession = {
          ...nextSession,
          ...(country ? { country } : {}),
          ...(xsollaLocale ? { xsollaLocale } : {}),
        };
        saveSession(sessionWithPreferences);
        setSession(sessionWithPreferences);
        void navigate(nextRoute, { replace: true });
      })
      .catch(() => {
        setError(handoffText.openFromAppMessage);
      });
  }, [defaultNext, locale, navigate, setLocale, setSession]);

  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-6 w-6" aria-hidden="true" />}
        title={text.openFromAppTitle}
      >
        {error}
      </EmptyState>
    );
  }

  return (
    <EmptyState
      icon={<LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />}
      title={text.openingShop}
    />
  );
}

function BalanceStrip({
  balance,
  locale,
}: {
  balance: WalletBalance | null;
  locale: SupportedLocale;
}) {
  const text = messages[locale];
  const count = balance ? String(balance.total) : "-";
  return (
    <div
      className="shop-balance-strip"
      aria-label={formatMessage(text.totalGems, { count })}
      style={
        {
          "--shop-balance-bg": `url("${gemAsset("ui_header_base_box.png")}")`,
          "--shop-balance-icon": `url("${gemAsset("ui_header_icon_gem.png")}")`,
        } as React.CSSProperties
      }
    >
      <span className="shop-balance-gem-icon" aria-hidden="true" />
      <span className="shop-stroked-text shop-balance-value">{count}</span>
    </div>
  );
}

function GemsRibbon({ locale }: { locale: SupportedLocale }) {
  const text = messages[locale];
  return (
    <div
      className="gems-ribbon"
      aria-label={text.gems}
      style={
        {
          "--ribbon-image": `url("${gemAsset("ribbon.png")}")`,
        } as React.CSSProperties
      }
    >
      <span className="shop-stroked-text gems-ribbon-text">{text.gems}</span>
    </div>
  );
}

function GemProductCard({
  product,
  offer,
  busy,
  disabled,
  locale,
  onBuy,
}: {
  product: ProductView;
  offer?: WebShopOffer;
  busy: boolean;
  disabled: boolean;
  locale: SupportedLocale;
  onBuy: () => void;
}) {
  const text = messages[locale];
  const priceLabel = getOfferButtonLabel(locale, text.unavailable, offer);
  const displayName =
    offer?.display_name?.trim() ||
    text.productTitles[product.offerCode] ||
    product.title;
  const gemsLabel = getOfferSubName(offer) ?? String(product.gems);
  const popLabel = getOfferPop(offer);
  return (
    <article
      className="gem-product-card"
      data-interactive={disabled ? undefined : "true"}
      style={
        {
          "--gem-card-main": `url("${gemAsset("shop_base_main.png")}")`,
          "--gem-card-outline": `url("${gemAsset("shop_base_outline_bk.png")}")`,
          "--gem-card-top": `url("${gemAsset("shop_base_above.png")}")`,
          "--gem-card-bottom": `url("${gemAsset("shop_base_bottom.png")}")`,
          "--gem-icon": `url("${gemAsset(product.image)}")`,
        } as React.CSSProperties
      }
    >
      <button
        type="button"
        aria-busy={busy}
        disabled={disabled}
        onClick={onBuy}
        aria-label={formatMessage(text.buyOfferFor, {
          name: displayName,
          price: priceLabel,
        })}
        className="gem-card-shell"
      >
        <div className="gem-card-inner">
          <div className="gem-card-upper-bg" aria-hidden="true" />
          <div className="shop-stroked-text gem-card-amount" data-text={gemsLabel}>
            {gemsLabel}
          </div>
          <div className="gem-card-art" aria-hidden="true" />
          <div className="game-label gem-card-name">{displayName}</div>
          <span className="shop-stroked-text gem-card-price-button" aria-hidden="true">
            {priceLabel}
          </span>
        </div>
        {popLabel && (
          <div className="gem-card-pop" aria-hidden="true">
            <img src={gemAsset("pop.webp")} />
            <span className="gem-card-pop-text">{popLabel}</span>
          </div>
        )}
      </button>
    </article>
  );
}

function ShopPage({
  session,
  backToGameUrl,
  locale,
}: {
  session: ShopSession | null;
  backToGameUrl: string | null;
  locale: SupportedLocale;
}) {
  const [offers, setOffers] = useState<WebShopOffer[]>([]);
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyOffer, setBusyOffer] = useState<string | null>(null);
  const text = messages[locale];
  const displayName = session?.user.display_name?.trim() || "-";

  const backToGame = () => {
    if (backToGameUrl) {
      window.location.assign(backToGameUrl);
    }
  };

  const products = useMemo(() => {
    const offerMap = new Map(offers.map((offer) => [offer.offer_code, offer]));
    return PRODUCTS.map((product) => ({
      ...product,
      gems: offerMap.get(product.offerCode)?.tokens_units ?? product.gems,
      offer: offerMap.get(product.offerCode),
    })).filter((product) => product.offer && allowedOfferCodes.has(product.offerCode));
  }, [offers]);

  useEffect(() => {
    if (!session) return;
    setLoading(true);
    Promise.all([
      listWebShopOffers(session, {
        country: session.country ?? "US",
        locale: session.xsollaLocale ?? locale,
      }),
      getBalance(session),
    ])
      .then(([nextOffers, nextBalance]) => {
        setOffers(nextOffers);
        setBalance(nextBalance);
        setError(null);
      })
      .catch(() => {
        setError(text.loadShopFailed);
      })
      .finally(() => setLoading(false));
  }, [locale, session, text.loadShopFailed]);

  if (!session) return <Navigate to="/shop/handoff" replace />;

  const startCheckout = async (offer: WebShopOffer) => {
    const offerCode = offer.offer_code;
    setBusyOffer(offerCode);
    setError(null);
    try {
      const checkout = await createCheckout(
        session,
        offerCode,
        offer.price?.currency,
        {
          country: session.country,
          language: session.xsollaLocale ?? locale,
        },
      );
      saveLastOrder(checkout.order_no);
      window.location.assign(checkout.checkout.redirect_url);
    } catch {
      setError(text.checkoutStartFailed);
      setBusyOffer(null);
    }
  };

  return (
    <>
      <section className="shop-topbar">
        <Link to="/" className="shop-header-link" aria-label={text.officialShop}>
          <img
            className="shop-header-banner"
            src={gemAsset(getHeaderBannerImage(locale))}
            alt={text.officialShop}
          />
        </Link>
      </section>
      <section className="shop-player-summary">
        <div className="shop-stroked-text shop-display-name" title={displayName}>
          {displayName}
        </div>
        <BalanceStrip balance={balance} locale={locale} />
      </section>
      {error ? (
        <div className="shop-error mb-4 flex items-center gap-2 px-3 py-2 text-sm text-white">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}
      {loading ? (
        <EmptyState
          icon={<LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />}
          title={text.loadingPacks}
        />
      ) : (
        <>
          <section className="gems-panel">
            <GemsRibbon locale={locale} />
            <div className="gems-grid">
              {products.map((product) => (
                <GemProductCard
                  key={product.offerCode}
                  product={product}
                  offer={product.offer}
                  busy={busyOffer === product.offerCode}
                  locale={locale}
                  disabled={
                    Boolean(busyOffer) ||
                    !product.offer?.price ||
                    product.offer.can_be_bought === false
                  }
                  onBuy={() => {
                    if (product.offer) void startCheckout(product.offer);
                  }}
                />
              ))}
            </div>
          </section>
          <div className="shop-footer mt-4">
            <button
              type="button"
              disabled={!backToGameUrl}
              onClick={backToGame}
              className="shop-back-game shop-stroked-text"
            >
              {text.backToGame}
            </button>
          </div>
          {busyOffer ? (
            <div className="shop-checkout-overlay" aria-live="polite" aria-busy="true">
              <LoaderCircle className="h-10 w-10 animate-spin" aria-hidden="true" />
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

function ReturnPage({
  session,
  backToGameUrl,
  locale,
}: {
  session: ShopSession | null;
  backToGameUrl: string | null;
  locale: SupportedLocale;
}) {
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const text = messages[locale];

  useEffect(() => {
    if (!session) return;
    const orderNo = loadLastOrder();
    if (!orderNo) {
      setError(text.noRecentOrder);
      return;
    }

    let canceled = false;
    const deadline = Date.now() + 90_000;

    const poll = async () => {
      while (!canceled && Date.now() < deadline) {
        const nextOrder = await getOrderStatus(session, orderNo);
        if (canceled) return;
        setOrder(nextOrder);
        if (nextOrder.status === "fulfilled") {
          const nextBalance = await getBalance(session);
          if (canceled) return;
          setBalance(nextBalance);
          return;
        }
        if (nextOrder.status === "refunded" || nextOrder.status === "chargeback") {
          setError(text.paymentFailed);
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 2500));
        if (canceled) return;
      }
      if (!canceled) {
        setError(text.paymentProcessing);
      }
    };

    poll().catch(() => {
      if (canceled) return;
      setError(text.verifyOrderFailed);
    });
    return () => {
      canceled = true;
    };
  }, [
    session,
    text.noRecentOrder,
    text.paymentFailed,
    text.paymentProcessing,
    text.verifyOrderFailed,
  ]);

  if (!session) return <Navigate to="/shop/handoff" replace />;

  if (order?.status === "fulfilled") {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-6 w-6" aria-hidden="true" />}
        title={text.gemsDelivered}
      >
        <div className="space-y-3">
          <div>
            {formatMessage(text.gemsAdded, {
              count: String(order.tokens_units),
            })}
          </div>
          <div className="text-[#fff3ff]">
            {formatMessage(text.balance, {
              count: balance ? String(balance.total) : "-",
            })}
          </div>
          <button
            type="button"
            className="shop-link-button inline-flex h-10 items-center px-4 font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!backToGameUrl}
            onClick={() => {
              if (backToGameUrl) window.location.assign(backToGameUrl);
            }}
          >
            {text.backToGame}
          </button>
        </div>
      </EmptyState>
    );
  }

  return (
    <EmptyState
      icon={
        error ? (
          <AlertCircle className="h-6 w-6" aria-hidden="true" />
        ) : (
          <RefreshCw className="h-6 w-6 animate-spin" aria-hidden="true" />
        )
      }
      title={error ? text.orderPending : text.confirmingPayment}
    >
      <div className="space-y-3">
        <div>{error ?? text.waitingPayment}</div>
        <Link className="shop-link-button inline-flex h-10 items-center px-4 font-black text-white" to="/shop">
          {text.backToShop}
        </Link>
      </div>
    </EmptyState>
  );
}

function CancelPage({ locale }: { locale: SupportedLocale }) {
  const text = messages[locale];
  return (
    <EmptyState
      icon={<AlertCircle className="h-6 w-6" aria-hidden="true" />}
      title={text.checkoutNotCompleted}
    >
      <Link className="shop-link-button mt-2 inline-flex h-10 items-center px-4 font-black text-white" to="/shop">
        {text.backToShop}
      </Link>
    </EmptyState>
  );
}

export default function App() {
  const { session, setSession } = useSession();
  const [backToGameUrl, setBackToGameUrl] = useState<string | null>(null);
  const [locale, setLocale] = useState<SupportedLocale>(() =>
    resolveInitialLocale(),
  );

  useEffect(() => {
    if (!session) {
      setBackToGameUrl(null);
      return;
    }
    let canceled = false;
    getWebShopConfig(session)
      .then((config) => {
        if (!canceled) setBackToGameUrl(config.back_to_game_url);
      })
      .catch(() => {
        if (!canceled) setBackToGameUrl(null);
      });
    return () => {
      canceled = true;
    };
  }, [session]);

  return (
    <Routes>
      <Route path="/" element={<HomePage locale={locale} session={session} />} />
      <Route
        path="/handoff"
        element={
          <Shell locale={locale}>
            <HandoffPage
              setSession={setSession}
              setLocale={setLocale}
              defaultNext="/"
              locale={locale}
            />
          </Shell>
        }
      />
      <Route
        path="/shop/handoff"
        element={
          <Shell locale={locale}>
            <HandoffPage
              setSession={setSession}
              setLocale={setLocale}
              defaultNext="/shop"
              locale={locale}
            />
          </Shell>
        }
      />
      <Route
        path="/shop/return"
        element={
          <Shell locale={locale}>
            <ReturnPage
              session={session}
              backToGameUrl={backToGameUrl}
              locale={locale}
            />
          </Shell>
        }
      />
      <Route
        path="/shop/cancel"
        element={
          <Shell locale={locale}>
            <CancelPage locale={locale} />
          </Shell>
        }
      />
      <Route
        path="/shop"
        element={
          <Shell locale={locale}>
            <ShopPage
              session={session}
              backToGameUrl={backToGameUrl}
              locale={locale}
            />
          </Shell>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
